import React from 'react';
import { connect } from "react-redux";
import { withRouter } from '../../../shared/hoc/withRouter';
import DataGridBase from '../../../partials/layout/DataGridBase';
import { Portlet, PortletBody } from "../../../partials/content/Portlet";
import Common from '../../../shared/common';
import { FormattedMessage, injectIntl } from "react-intl";
import { Alert } from "react-bootstrap";
import { GetAccessRights } from "../../../router/Protector/Protector";
import * as contractsCrud from '../../../crud/contracts.crud';
import { GetCurrencyCodes, getContractPartyStructureTypes, contractTagCategories } from '../../../shared/lookup';
import * as companiesCrud from '../../../crud/companies.crud';
import * as contractTypeCrud from '../../../crud/contractType.crud';
import * as tagCrud from '../../../crud/tag.crud';
import * as asiteCrud from '../../../crud/asite.crud';
import PortletSpinner from "../../../partials/layout/PortletSpinner";
import * as contractsDuck from '../../../store/ducks/contracts.duck';
import { withSnackbar } from 'notistack';
import { listAccountsAllowedRegisterBatchOperations } from '../../../router/Protector/AccessExceptions';
import SystemHelper from "../../../shared/systemHelper";


const isFinancialLimitEnabled = process.env.REACT_APP_FINANCIAL_LIMIT_ENABLED === "true";
const isAsiteEnabled = process.env.REACT_APP_ATTACHMENT_MODE === 'ASITE';
const contractTagsEnabled = process.env.REACT_APP_CONTRACT_COMPANY_TAGS_ENABLED === 'true';
const cacheWarningEnabled = process.env.REACT_APP_SHOW_CONTRACTS_CACHE_WARNING === 'true';
const isPackagedContractsEnabled = process.env.REACT_APP_PACKAGED_CONTRACTS_ENABLED === "true";

class ContractsGrid extends React.Component {
    constructor(props) {
        super(props);
        this.canCreateNew = GetAccessRights("/contracts/new-contract", this.props.userProfile).allowAccess;

        this.currencyCodes = GetCurrencyCodes(props.intl);

        this.contractPartyStructureTypes = getContractPartyStructureTypes(this.props.intl);

        this.isForecastEnabled = SystemHelper.getSettingValueBoolean(SystemHelper.SETTINGS.FORECASTS_ENABLED);

        this.batchOperationsEnabled = false;
        if (process.env.REACT_APP_BATCH_OPERATIONS_ENABLED === "true") {
            //REGISTER SUPPORTS BATCH OPERATION
            if (listAccountsAllowedRegisterBatchOperations.includes(this.props.userProfile.accounttype)) {
                this.batchOperationsEnabled = true;
            }
        }

        this.state = {
            optCompanies: [],
            optCompaniesFiltered: [],
            asiteUsers: null,
            asiteWorkspaces: null,
            contractTags: {},
            contractTypes: [],
            formFields: null
        }
    }

    getFormFieldsOnceDownloadTemplateForBatchButtonClicked = async () => {
        try {
            if (isAsiteEnabled) {
                const [
                    optCompaniesFiltered,
                    optCompanies,
                    asiteUsers,
                    asiteWorkspaces,
                    contractTags,
                    contractTypes
                ] = await Promise.all([
                    this.getCompanyOptions(this.props.userProfile),
                    this.getCompanyOptions(),
                    this.fetchAsiteUsers(),
                    this.fetchAsiteWorkspaces(),
                    this.getTags(0),
                    this.getContractTypes()
                ]);

                await new Promise(resolve =>
                    this.setState(
                        {
                            optCompaniesFiltered,
                            optCompanies,
                            asiteUsers,
                            asiteWorkspaces,
                            contractTags,
                            contractTypes
                        },
                        resolve
                    )
                );

                const formFields = this.getFormConfig();
                return formFields;

            } else {
                const [optCompaniesFiltered, optCompanies, contractTags, contractTypes] =
                    await Promise.all([
                        this.getCompanyOptions(this.props.userProfile),
                        this.getCompanyOptions(),
                        this.getTags(0),
                        this.getContractTypes()
                    ]);

                await new Promise(resolve =>
                    this.setState(
                        {
                            optCompaniesFiltered,
                            optCompanies,
                            contractTags,
                            contractTypes
                        },
                        resolve
                    )
                );

                const formFields = this.getFormConfig();
                return formFields;
            }
        } catch (err) {
            console.error(err);
        }
    };

    getCompanyOptions = (userProfile) => {
        return new Promise(async (resolve, reject) => {
            let { data: companiesList } = await companiesCrud.getCutDownCompaniesList(userProfile);
            if (!companiesList || !companiesList.length > 0) {
                this.props.enqueueSnackbar('No companies found', { variant: 'warning', });
                resolve([]);
            }
            resolve(companiesList);
        });
    }

    fetchAsiteUsers = () => {
        return new Promise(async (resolve, reject) => {
            let { data: asiteUsers } = await asiteCrud.getAsiteAPIUsers();
            if (!asiteUsers || !asiteUsers.length > 0) {
                this.props.enqueueSnackbar('No asite users found', { variant: 'warning', });
                resolve([]);
            }
            resolve(asiteUsers);
        });
    }

    fetchAsiteWorkspaces = () => {
        return new Promise(async (resolve, reject) => {
            let { data: asiteWorkspaces } = await asiteCrud.getAsiteWorkspaces();
            if (!asiteWorkspaces || !asiteWorkspaces.length > 0) {
                this.props.enqueueSnackbar('No asite workspaces found', { variant: 'warning', });
                resolve([]);
            }
            resolve(asiteWorkspaces);
        });
    }

    getTags = async (companyid) => {
        if (!contractTagsEnabled) { return {}; }
        try {
            const { data: tags } = await tagCrud.GetTagsForCategory(contractTagCategories, companyid);
            const contractTags = Common.GroupBy(tags, 'category');
            return contractTags;
        } catch (err) {
            this.props.enqueueSnackbar(err.toString(), { variant: 'error', });
        }
    }

    getContractTypes = () => {
        return new Promise(async (resolve, reject) => {
            let { data: contractTypes } = await contractTypeCrud.getContractTypes();
            if (!contractTypes || !contractTypes.length > 0) {
                this.props.enqueueSnackbar('No contract type found', { variant: 'warning', });
                resolve([]);
            }
            resolve(contractTypes);
        });
    }

    getFormConfig = () => {
        const formFields = [
            {
                caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.NUMBER' }),
                dataField: 'contractreferencenumber',
                required: true,
                dataType: 'integer'
            }
        ];

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.NAME' }),
            dataField: 'name',
            required: true,
            dataType: 'string'
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.COMPANY' }),
            dataField: 'companyid',
            required: true,
            dataType: 'list',
            options: {
                dataSource: this.state.optCompaniesFiltered,
                valueExpr: 'companyid',
                displayExpr: 'name'
            }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.PROJECT' }),
            dataField: 'projectid',
            required: true,
            dataType: 'integer'
            // dataType: 'list',
            // options: {
            //     dataSource: this.state.optProjects,
            //     valueExpr: 'projectid',
            //     displayExpr: 'display'
            // }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.TYPE' }),
            dataField: 'contracttypeid',
            required: true,
            dataType: 'list',
            options: {
                dataSource: this.state.contractTypes,
                valueExpr: 'ContractTypeID',
                displayExpr: 'Name'
            },
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.PARTY_STRUCTURE' }),
            dataField: 'partystructure',
            required: true,
            dataType: 'list',
            options: {
                dataSource: this.contractPartyStructureTypes,
                valueExpr: 'value',
                displayExpr: 'display'
            }
        });

        const templateModeOptions = [
            { value: 0, display: this.props.intl.formatMessage({ id: 'CONTRACT.CREATE.TEMPLATE_OPTIONS.COPY_MASTER' }) },
            { value: 1, display: this.props.intl.formatMessage({ id: 'CONTRACT.CREATE.TEMPLATE_OPTIONS.USE_MASTER' }) }
        ]

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.TEMPLATE_MODE_CREATE' }),
            dataField: 'usemastertemplate',
            required: false,
            dataType: 'list',
            options: {
                dataSource: templateModeOptions,
                valueExpr: 'value',
                displayExpr: 'display'
            }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.TEMPLATE' }),
            dataField: 'mastertemplateid',
            required: true,
            dataType: 'integer'
            // dataType: 'list',
            // options: {
            //     dataSource: this.state.optTemplates,
            //     valueExpr: 'templateid',
            //     displayExpr: 'name'
            // }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.PO_NUMBER' }),
            dataField: 'versionnumber',
            required: false,
            dataType: 'integer'
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.PO_VALUE' }),
            dataField: 'defectinterval',
            required: false,
            dataType: 'integer'
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.DEFECTS_PERIOD' }),
            dataField: 'scope',
            required: false,
            dataType: 'integer'
        });

        if (contractTagsEnabled) {

            //const options = [{ value: 1, display: this.props.intl.formatMessage({ id: "GENERAL.FORMS.SWITCH_YES" })}, { value: 0, display: this.props.intl.formatMessage({ id: "GENERAL.FORMS.SWITCH_NO" })}];

            // formFields.push({
            //     caption: this.props.intl.formatMessage({ id: 'COMPANY.FIELD.COMPANY_ENABLECOMPANYUSERS' }),
            //     dataField: 'enablecompanyusers',
            //     required: true,
            //     dataType: 'list',
            //     options: {
            //         dataSource: options,
            //         valueExpr: 'value',
            //         displayExpr: 'display'
            //     }
            // });

            formFields.push({
                caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.AREAS' }),
                dataField: 'areaids',
                required: false,
                dataType: 'multi',
                options: {
                    dataSource: this.state.contractTags?.Areas,
                    valueExpr: 'tagid',
                    displayExpr: 'name'
                }
            });

            formFields.push({
                caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.HUBS' }),
                dataField: 'hubids',
                required: false,
                dataType: 'multi',
                options: {
                    dataSource: this.state.contractTags?.Hubs,
                    valueExpr: 'tagid',
                    displayExpr: 'name'
                }
            });

            formFields.push({
                caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.LOTS' }),
                dataField: 'lotids',
                required: false,
                dataType: 'multi',
                options: {
                    dataSource: this.state.contractTags?.Lots,
                    valueExpr: 'tagid',
                    displayExpr: 'name'
                }
            });

            formFields.push({
                caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.FRAMEWORKS' }),
                dataField: 'frameworkids',
                required: false,
                dataType: 'multi',
                options: {
                    dataSource: this.state.contractTags?.Frameworks,
                    valueExpr: 'tagid',
                    displayExpr: 'name'
                }
            });

            formFields.push({
                caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.SECURITYGROUPS' }),
                dataField: 'securitygroupids',
                required: false,
                dataType: 'multi',
                options: {
                    dataSource: this.state.contractTags?.SecurityGroups,
                    valueExpr: 'tagid',
                    displayExpr: 'name'
                }
            });

            formFields.push({
                caption: this.props.intl.formatMessage({ id: 'GENERAL.FORMS.FIELD.OTHER' }),
                dataField: 'othercontracttagids',
                required: false,
                dataType: 'multi',
                options: {
                    dataSource: this.state.contractTags?.ContractOther,
                    valueExpr: 'tagid',
                    displayExpr: 'name'
                }
            });
        }

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.CLIENT' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.COMPANY' }),
            dataField: 'employercompanyid',
            required: false,
            dataType: 'list',
            options: {
                dataSource: this.state.optCompanies,
                valueExpr: 'companyid',
                displayExpr: 'name'
            },
            dependOn: {
                field: 'partystructure',
                expectedValues: ['Client > Contract Administrator > Supplier', 'Client > Contract Administrator > Supervisor > Supplier']
            }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.CLIENT' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.EMPLOYER.USER' }),
            dataField: 'useremployercompanyid',
            required: false,
            dataType: 'list',
            options: {
                dataSource: this.state.optCompanies,
                valueExpr: 'companyid',
                displayExpr: 'name'
            },
            dependOn: {
                field: 'partystructure',
                expectedValues: ['Client > Contract Administrator > Supplier', 'Client > Contract Administrator > Supervisor > Supplier']
            }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.CLIENT' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.USER' }),
            dataField: 'employerid',
            required: false,
            dataType: 'integer',
            dependOn: {
                field: 'partystructure',
                expectedValues: ['Client > Contract Administrator > Supplier', 'Client > Contract Administrator > Supervisor > Supplier']
            }
            // dataType: 'list',
            // options: {
            //     dataSource: this.state.optEmployerUsers,
            //     valueExpr: 'accountid',
            //     displayExpr: 'display'
            // }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.PM' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.COMPANY' }),
            dataField: 'pmcompanyid',
            required: true,
            dataType: 'list',
            options: {
                dataSource: this.state.optCompanies,
                valueExpr: 'companyid',
                displayExpr: 'name'
            }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.PM' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.PM.USER' }),
            dataField: 'userpmcompanyid',
            required: true,
            dataType: 'list',
            options: {
                dataSource: this.state.optCompanies,
                valueExpr: 'companyid',
                displayExpr: 'name'
            }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.PM' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.USER' }),
            dataField: 'projectmanagerid',
            required: true,
            dataType: 'integer'
            // dataType: 'list',
            // options: {
            //     dataSource: this.state.optPMUsers,
            //     valueExpr: 'accountid',
            //     displayExpr: 'display'
            // }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.SUPPLIER' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.COMPANY' }),
            dataField: 'contractorcompanyid',
            required: true,
            dataType: 'list',
            options: {
                dataSource: this.state.optCompanies,
                valueExpr: 'companyid',
                displayExpr: 'name'
            }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.SUPPLIER' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.CONTRACTOR.USER' }),
            dataField: 'usercontractcompanyid',
            required: true,
            dataType: 'list',
            options: {
                dataSource: this.state.optCompanies,
                valueExpr: 'companyid',
                displayExpr: 'name'
            }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.SUPPLIER' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.USER' }),
            dataField: 'contractmanagerid',
            required: true,
            dataType: 'integer'
            // dataType: 'list',
            // options: {
            //     dataSource: this.state.optContractorUsers,
            //     valueExpr: 'accountid',
            //     displayExpr: 'display'
            // }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.SUPERVISOR' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.COMPANY' }),
            dataField: 'supervisorcompanyid',
            required: false,
            dataType: 'list',
            options: {
                dataSource: this.state.optCompanies,
                valueExpr: 'companyid',
                displayExpr: 'name'
            },
            dependOn: {
                field: 'partystructure',
                expectedValues: ['Client > Contract Administrator > Supervisor > Supplier', 'Contract Administrator > Supervisor > Supplier']
            }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.SUPERVISOR' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.SUPERVISOR.USER' }),
            dataField: 'usersupervisorcompanyid',
            required: false,
            dataType: 'list',
            options: {
                dataSource: this.state.optCompanies,
                valueExpr: 'companyid',
                displayExpr: 'name'
            },
            dependOn: {
                field: 'partystructure',
                expectedValues: ['Client > Contract Administrator > Supervisor > Supplier', 'Contract Administrator > Supervisor > Supplier']
            }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.SUPERVISOR' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.USER' }),
            dataField: 'supervisorid',
            required: false,
            dataType: 'integer',
            dependOn: {
                field: 'partystructure',
                expectedValues: ['Client > Contract Administrator > Supervisor > Supplier', 'Contract Administrator > Supervisor > Supplier']
            }
            // dataType: 'list',
            // options: {
            //     dataSource: this.state.optSupervisorUsers,
            //     valueExpr: 'accountid',
            //     displayExpr: 'display'
            // }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'GENERAL.FORMS.FIELD.START_DATE' }),
            dataField: 'startdatedisplay',
            required: true,
            dataType: 'date'
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.CMP_DATE' }),
            dataField: 'completedatedisplay',
            required: true,
            dataType: 'date'
        });
        if (isPackagedContractsEnabled) {
            formFields.push({
                caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.PACKAGED' }),
                dataField: 'ispackaged',
                required: true,
                dataType: 'bool',
            });
        }


        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.CURRENCY' }),
            dataField: 'currency',
            required: true,
            dataType: 'list',
            options: {
                dataSource: this.currencyCodes,
                valueExpr: 'code',
                displayExpr: 'name'
            }
        });

        formFields.push({
            caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.PRICE' }),
            dataField: 'contractvalue',
            required: true,
            dataType: 'integer'
        });

        const options = [{ value: 1, display: this.props.intl.formatMessage({ id: "GENERAL.FORMS.SWITCH_YES" }) }, { value: 0, display: this.props.intl.formatMessage({ id: "GENERAL.FORMS.SWITCH_NO" }) }];



        if (this.canCreateNew) {
            formFields.push({
                caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.ISDEMOCONTRACT' }),
                dataField: 'isdemocontract',
                required: false,
                dataType: 'list',
                options: {
                    dataSource: options,
                    valueExpr: 'value',
                    displayExpr: 'display'
                }
            });
        }

        if (isFinancialLimitEnabled) {
            formFields.push({
                caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.CONTRACTFINACIALLIMITENABLED' }),
                dataField: 'financiallimitenabled',
                required: false,
                dataType: 'list',
                options: {
                    dataSource: options,
                    valueExpr: 'value',
                    displayExpr: 'display'
                }
            });
        }

        if (isAsiteEnabled) {
            formFields.push({
                caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.ASITE' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.WORKSPACE' }),
                dataField: 'workspaceid',
                required: false,
                dataType: 'list',
                options: {
                    dataSource: this.state.asiteWorkspaces,
                    valueExpr: 'id',
                    displayExpr: 'description'
                }
            });

            formFields.push({
                caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.ASITE' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.FOLDERID' }),
                dataField: 'folderid',
                required: false,
                dataType: 'integer'
            });

            formFields.push({
                caption: this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.ASITE' }) + ' ' + this.props.intl.formatMessage({ id: 'CONTRACT.FIELD.APIUSER' }),
                dataField: 'apiuser',
                required: false,
                dataType: 'list',
                options: {
                    dataSource: this.state.asiteUsers
                }
            });
        }

        return formFields;
    }

    onTableSelectionChanged = ({ selectedRowsData }) => {
        if (selectedRowsData.length > 0) {
            const data = selectedRowsData[0];
            this.props.navigate("/contract/" + data.contractid + process.env.REACT_APP_CONTRACT_HOMEPAGE);
        }
    }
    calculateImplementedCEValue = (rowData) => {
        return rowData.currentcontractvalue - rowData.contractvalue;
    }
    calculateContractValueOutstanding = (rowData) => {
        return rowData.currentcontractvalue - rowData.totalcertified;
    }
    calculateContractState = (rowData) => {
        if (rowData.deleted === 1) {
            return this.props.intl.formatMessage({ id: 'OPTIONS.STATUS.DELETED' });
        }
        else {
            switch (rowData.recordstatus) {
                case 1:
                    return this.props.intl.formatMessage({ id: 'OPTIONS.STATUS.ACTIVE' });
                case 2:
                    return this.props.intl.formatMessage({ id: 'OPTIONS.STATUS.COMPLETED' });
                case 3:
                    return this.props.intl.formatMessage({ id: 'OPTIONS.STATUS.ARCHIVED' });
                default:
                    return 'Unknown';
            }
        }
    }
    renderImplementedCEValue = (cellInfo) => {
        return Common.FormatCurrencyNum((cellInfo.data.currentcontractvalue - cellInfo.data.contractvalue), cellInfo.data.currency);
    }
    renderContractValueOutstanding = (cellInfo) => {
        return Common.FormatCurrencyNum((cellInfo.data.currentcontractvalue - cellInfo.data.totalcertified), cellInfo.data.currency);
    }

    renderOriginalPrice = (cellInfo) => {
        return Common.FormatCurrencyNum(cellInfo.data.contractvalue, cellInfo.data.currency);
    }
    renderCurrentPrice = (cellInfo) => {
        return Common.FormatCurrencyNum(cellInfo.data.currentcontractvalue, cellInfo.data.currency);
    }
    renderCostForecast = (cellInfo) => {
        return Common.FormatCurrencyNum(cellInfo.data.forecastedcost, cellInfo.data.currency);
    }
    renderTotalCertified = (cellInfo) => {
        return Common.FormatCurrencyNum(cellInfo.data.totalcertified, cellInfo.data.currency);
    }
    renderPOValue = (cellInfo) => {
        return Common.FormatCurrencyNum(cellInfo.data.scope, cellInfo.data.currency);
    }
    // editorPreparing = (e) => {
    //     if (e.parentType === "filterRow" && (e.dataField === "area" || e.dataField === "hub" || e.dataField === "framework" || e.dataField === "lot")) {
    //         e.cancel = true;
    //     }
    // }
    render() {
        if (!this.props.contracts) {
            return (<PortletSpinner />);
        }

        const columns = [
            { resource: "CONTRACT.FIELD.NAME", dataField: "name", fixed: true },
            { resource: "CONTRACT.FIELD.PROJECT_NAME", dataField: "project" },
            { resource: "CONTRACT.FIELD.TYPE", dataField: "contracttype" },
            { resource: "CONTRACT.FIELD.STATUS", dataField: "recordstatus", calculateCellValue: this.calculateContractState },
            { resource: "CONTRACT.FIELD.SUPPLIER", dataField: "contractorcompany" },
            { resource: "CONTRACT.FIELD.ORIGINAL_CONTRACT_PRICE", dataField: "contractvalue", dataType: "currency", cellRender: this.renderOriginalPrice },
            { resource: "CONTRACT.FIELD.CURRENT_CONTRACT_PRICE", dataField: "currentcontractvalue", dataType: "currency", cellRender: this.renderCurrentPrice },
            { resource: "GENERAL.FORMS.FIELD.START_DATE", dataField: "startdate", dataType: "date", visible: false },
            { resource: "CONTRACT.FIELD.ORIGINAL_CMP_DATE", dataField: "completedate", dataType: "date", visible: false },
            { resource: "CONTRACT.FIELD.CURRENT_CMP_DATE", dataField: "completiondate", dataType: "date", visible: false },
            { resource: "CONTRACT.FIELD.TOTAL_CERTIFIED", dataField: "totalcertified", visible: false, dataType: "currency", cellRender: this.renderTotalCertified },
            { resource: "CONTRACT.FIELD.LEAD_CONSULTANT", dataField: "contractmanager", visible: false },
            { resource: "CONTRACT.FIELD.LEAD_CLIENT", dataField: "employer", visible: false },
            { resource: "CONTRACT.FIELD.LEAD_PM", dataField: "projectmanager", visible: false },
            { resource: "CONTRACT.PRICE_CHANGE", dataField: "implemented", calculateCellValue: this.calculateImplementedCEValue, visible: false, dataType: "currency", cellRender: this.renderImplementedCEValue },
            { resource: "CONTRACT.FIELD.VALUE_OUTSTANDING", dataField: "outstanding", calculateCellValue: this.calculateContractValueOutstanding, visible: false, dataType: "currency", cellRender: this.renderContractValueOutstanding },
            { resource: "GENERAL.FORMS.FIELD.CURRENCY", dataField: "currency", visible: false },
            { resource: "CONTRACT.FIELD.PO_NUMBER", dataField: "versionnumber", visible: false },
            { resource: "CONTRACT.FIELD.PO_VALUE", dataField: "scope", visible: false, dataType: "currency", cellRender: this.renderPOValue },
            { resource: "PROJECT.FIELD.CODE", dataField: "projectcode", visible: false },
            { resource: "GENERAL.FORMS.ACTUAL_COMPLETION_DATE", dataField: "actualcompletiondate", dataType: "date", visible: false },
            { resource: "CONTRACT.FIELD.ID", dataField: "contractid", visible: false },
            { resource: "CONTRACT.FIELD.NUMBER", dataField: "contractreferencenumber", visible: false },
            { resource: "CONTRACT.FIELD.PARTY_STRUCTURE", dataField: "partystructure", lookup: { dataSource: this.contractPartyStructureTypes, displayExpr: 'display', valueExpr: 'value', }, visible: false },
            { resource: "CONTRACT.FIELD.TEMPLATE_TYPE", dataField: "templatetype", visible: true },
            { resource: "CONTRACT.FIELD.TEMPLATE_NAME", dataField: "templatename", visible: true },
            { resource: "GENERAL.FORMS.DEFECTS_DATE", dataField: "defectsdate", dataType: "date", visible: false },
            { resource: "CONTRACT.FIELD.PAC_SUPPLIER", dataField: "pac_supplier", visible: false },
            { resource: "CONTRACT.FIELD.PAC_PM", dataField: "pac_pm", visible: false },
            { resource: "CONTRACT.FIELD.PAC_SUPERVISOR", dataField: "pac_supervisor", visible: false },
            { resource: "CONTRACT.FIELD.PAC_CLIENT", dataField: "pac_client", visible: false },
            { resource: "CONTRACT.FIELD.PAC_ALL", dataField: "pac_all", visible: false },
            { resource: "GENERAL.FORMS.LAST_COMMUNICATED_DATE", dataField: "lastcommunicateddate", dataType: "date", visible: false },
            { resource: "GENERAL.FORMS.EXPORTED_DATE", dataField: "exporteddate", dataType: "date", visible: false },
            { resource: "CONTRACT.FIELD.PROJECTCOMPANY", dataField: "projectcompany", visible: false },
        ];
        if (this.isForecastEnabled) {
            columns.push(
                { resource: "GENERAL.FORMS.FIELD.COSTFORECAST", dataField: "forecastedcost", visible: false, dataType: 'currency', cellRender: this.renderCostForecast }
            )
        }
        if (isPackagedContractsEnabled) {
            columns.push(
                { resource: "CONTRACT.FIELD.PACKAGED", dataField: "ispackaged", visible: false }
            )
        }
        if (contractTagsEnabled) {
            columns.push(
                { resource: "CONTRACT.FIELD.AREAS", dataField: "tags_area", visible: false, dataType: 'tag', headerFilter: { dataSource: this.props.areasFilter } },
                { resource: "CONTRACT.FIELD.HUBS", dataField: "tags_hub", visible: false, dataType: 'tag', headerFilter: { dataSource: this.props.hubsFilter } },
                { resource: "CONTRACT.FIELD.FRAMEWORKS", dataField: "tags_framework", visible: false, dataType: 'tag', headerFilter: { dataSource: this.props.frameworkFilter } },
                { resource: "CONTRACT.FIELD.LOTS", dataField: "tags_lot", visible: false, dataType: 'tag', headerFilter: { dataSource: this.props.lotFilter } },
                { resource: "GENERAL.FORMS.FIELD.OTHER", dataField: "tags_other", visible: false, dataType: 'tag', headerFilter: { dataSource: this.props.otherTagFilter } },
                { resource: "CONTRACT.FIELD.SECURITYGROUPS", dataField: "tags_securityGroup", visible: false, dataType: 'tag', headerFilter: { dataSource: this.props.securityGroupsFilter } }
            )
        }
        return (
            <Portlet style={{ width: "inherit" }}>
                <PortletBody>
                    {cacheWarningEnabled && (
                        <Alert variant="secondary">
                            <p>
                                <FormattedMessage id="CONTRACT.LIST.WARNING_CACHE" />
                            </p>
                        </Alert>
                    )}
                    <DataGridBase
                        version="8"
                        //BATCH OPS
                        batchOperations={this.batchOperationsEnabled}
                        //batchFormFields={this.state.formFields}//this field is null by default because we want to load formfields when download template button clicked
                        batchImportEndpoint={contractsCrud.importContracts}
                        batchGetFormFieldsWithDownloadTemplateButton={this.getFormFieldsOnceDownloadTemplateForBatchButtonClicked}
                        // batchImportEndpoint={(records) =>
                        //     contractsCrud.importContracts(records).then(res => {
                        //     console.log('Import successful — reloading contracts');
                        //     this.props.loadContracts();
                        //     return res; // Important – return the original result for grid to proceed
                        //     })
                        // }
                        onBatchRecordsUpdated={this.props.reloadContracts}
                        customFields={this.props.customFields}
                        //BATCH OPS
                        title={this.props.intl.formatMessage({ id: 'NAVIGATION.MY_CONTRACTS' })}
                        createPath={this.canCreateNew ? '/contracts/new-contract' : null}
                        name="Contracts"
                        dataSource={this.props.contracts}
                        keyExpr="contractid"
                        onSelectionChanged={this.onTableSelectionChanged}
                        hoverStateEnabled={true}
                        rowAlternationEnabled={false}
                        repaintAfterMounted={true}
                        isContract={true}
                        columns={columns}
                        onEditorPreparing={this.editorPreparing}
                    />
                </PortletBody>
            </Portlet>
        );
    }
}

const mapStateToProps = store => ({
    userProfile: store.auth.userProfile,
    areasFilter: store.contracts.areasFilter,
    hubsFilter: store.contracts.hubsFilter,
    frameworkFilter: store.contracts.frameworkFilter,
    lotFilter: store.contracts.lotFilter,
    otherTagFilter: store.contracts.otherTagFilter,
});

const mapDispatchToProps = {
    loadContracts: contractsDuck.actions.fetchStart
}

export default injectIntl(withRouter(withSnackbar(connect(mapStateToProps, mapDispatchToProps)(ContractsGrid))));